import {
  AlertBox,
  BasicModal,
  Button,
  PhoneInput,
  TextInput,
} from '@life-line-lab/components-react';
import { first, isEmpty, isNil, omitBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PurchasedVoucher } from 'src/models/telemed-voucher';
import errorHandler from 'Utils/error-handler';
import VoucherAssignToSchema from './voucher-assign-modal.validation';

interface VoucherAssignModalProps {
  onSubmit: Function;
  vouchersToAssign: PurchasedVoucher[];
  showVoucherAssignModal: boolean;
  onCloseModal: () => void;
}

const VoucherAssignModal = ({ 
  onSubmit,
  vouchersToAssign,
  showVoucherAssignModal,
  onCloseModal,
}: VoucherAssignModalProps) => {
  const [error, setError] = useState<any>({});
  const [input, setInput] = useState<any>({});
  const [isReadonly, setIsReadonly] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setError({});
    setInput({});

    if (isEmpty(vouchersToAssign)) {
      setIsReadonly(false);
      return;
    }

    const firstVoucher = first(vouchersToAssign);
    const assignedComponent = firstVoucher?.assignedTo;

    if (isEmpty(assignedComponent)) {
      setIsReadonly(false);
      return;
    }

    setInput({
      email: assignedComponent?.email,
      fullName: assignedComponent?.fullName,
      contactNumber: {
        dialCode: assignedComponent?.contactNumber?.countryCode,
        number: assignedComponent?.contactNumber?.number,
      },
    });
    setIsReadonly(true);
  }, [vouchersToAssign]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError({});
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (value: { dialCode: string; number: string }, name: string) => {
    setError({});
    setInput({ ...input, [name]: value });
  };

  const handleSubmitAssignTo = () => {
    try {
      const value = VoucherAssignToSchema.validateSync(
        omitBy(
          {
            ...input,
          },
          isNil,
        ),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
        },
      );

      onSubmit(value);
    } catch (err) {
      let { path, refPath, value } = errorHandler.validation(err) || {};

      if (!isNil(refPath)) {
        setError({
          ...error,
          [path]: t(`${value}`, {
            field: t(`label.${path}`),
            refPath: refPath ? t(`label.${refPath}`) : null,
          }),
        });
      } else {
        setError({
          ...error,
          [path]: t(`${value}`, { field: t(`label.${path}`) }),
        });
      }
    }
  };

  return (
    <BasicModal
      title={!isReadonly ? t('action.assignTo') : t('label.assignedTo')}
      isClosable={true}
      isMaskClosable={true}
      isOpen={showVoucherAssignModal}
      onClose={onCloseModal}
    >
      <div className="">
        <div>
          <div className="my-20">
            <TextInput
              isError={!!error.email}
              isRequired={true}
              label={t('label.email')}
              name="email"
              value={input.email}
              placeholder="email@example.com"
              className={isReadonly ? 'hide-input-cursor' : undefined}
              autoComplete={isReadonly ? "off" : undefined}
              onChange={!isReadonly ? handleChange : undefined}
              />
            <AlertBox text={error.email} />
          </div>
          <div className="my-20">
            <TextInput
              isError={!!error.fullName}
              isRequired={false}
              label={t('label.fullName')}
              name="fullName"
              value={input.fullName}
              className={isReadonly ? 'hide-input-cursor' : undefined}
              autoComplete={isReadonly ? "off" : undefined}
              onChange={!isReadonly ? handleChange : undefined}
              />
            <AlertBox text={error.fullName} />
          </div>
          <div className="my-20">
            {isReadonly ? (
              <TextInput
              isRequired={false}
              label={t('label.contactNumber')}
              name="contactNumber"
              className={isReadonly ? 'hide-input-cursor' : undefined}
              autoComplete={isReadonly ? "off" : undefined}
              value={isEmpty(input.contactNumber?.number) ? '' : `+${input.contactNumber?.dialCode}${input.contactNumber?.number}`}
              onChange={undefined}
              />
            ) : (
              <PhoneInput
                isRequired={false}
                isError={!!error.contactNumber}
                label={t('label.contactNumber')}
                name="contactNumber"
                value={input.contactNumber}
                onChange={(value) => handlePhoneChange(value, 'contactNumber')}
              />
            )}
            <AlertBox text={error.contactNumber} />
            <AlertBox text={error['contactNumber.dialCode']} />
            <AlertBox text={error['contactNumber.number']} />
          </div>
          {!isReadonly && (
            <div
              className="mt-10"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Button
                type="secondary"
                label={
                  <React.Fragment>
                    <span>{t('action.back')}</span>
                  </React.Fragment>
                }
                size="large"
                onClick={onCloseModal}
              />
              <Button
                type="primary"
                label={
                  <React.Fragment>
                    <span>{t('action.continue')}</span>
                  </React.Fragment>
                }
                size="large"
                onClick={handleSubmitAssignTo}
              />
            </div>
          )}
        </div>
      </div>
    </BasicModal>
  );
};

export default VoucherAssignModal